import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { LogOut, Moon, Bell, Calendar, Plus, X, Sun, PartyPopper, Clock, Pencil, Trash2, Check, User } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/apiClient";

const COLOR_OPTIONS = [
  "#22C55E", // Green
  "#3B82F6", // Blue
  "#EC4899", // Pink
  "#F97316", // Orange
  "#06B6D4", // Cyan
  "#A855F7", // Purple
  "#EF4444", // Red
  "#F59E0B", // Amber
  "#10B981", // Emerald
  "#8B5CF6", // Violet
];

const TimeInput = ({ label, icon: Icon, value, onChange, iconColor }) => (
  <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 ${iconColor}`} />
      <span className="text-sm">{label}</span>
    </div>
    <input
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-background border border-border rounded-md px-2 py-1 text-sm w-24"
    />
  </div>
);

const SettingsModal = ({ open, onOpenChange, user, onSettingsUpdate, onLogout, categories, onCategoriesUpdate, onUserUpdate }) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState(COLOR_OPTIONS[0]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryColor, setEditCategoryColor] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const handleDarkModeToggle = (checked) => {
    onSettingsUpdate({ dark_mode: checked });
  };

  const handleRemindersToggle = (checked) => {
    onSettingsUpdate({ reminders: checked });
  };

  const handleWeekStartToggle = (checked) => {
    onSettingsUpdate({ week_starts_monday: checked });
  };

  const handleNotificationTimeChange = (field, value) => {
    onSettingsUpdate({ [field]: value });
  };

  const handleNameEdit = async () => {
    if (!editedName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    try {
      const response = await apiFetch('/user/name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editedName.trim() })
      });

      if (response.ok) {
        toast.success("Name updated!");
        setIsEditingName(false);
        if (onUserUpdate) onUserUpdate();
      } else {
        toast.error("Failed to update name");
      }
    } catch (error) {
      toast.error("Failed to update name");
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      const response = await apiFetch('/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCategoryName.trim(),
          color: newCategoryColor
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add category');
      }

      toast.success("Category added!");
      setNewCategoryName("");
      setNewCategoryColor(COLOR_OPTIONS[0]);
      setIsAddingCategory(false);
      
      // Refresh categories
      if (onCategoriesUpdate) {
        onCategoriesUpdate();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditCategory = async (key) => {
    if (!editCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    try {
      const response = await apiFetch(`/categories/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editCategoryName.trim(),
          color: editCategoryColor
        })
      });

      if (response.ok) {
        toast.success("Category updated!");
        setEditingCategory(null);
        if (onCategoriesUpdate) onCategoriesUpdate();
      } else {
        toast.error("Failed to update category");
      }
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleDeleteCategory = async (key) => {
    if (!confirm("Are you sure you want to delete this category?")) return;

    try {
      const response = await apiFetch(`/categories/${key}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success("Category deleted!");
        if (onCategoriesUpdate) onCategoriesUpdate();
      } else {
        toast.error("Failed to delete category");
      }
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const startEditingCategory = (key, cat) => {
    setEditingCategory(key);
    setEditCategoryName(cat.name);
    setEditCategoryColor(cat.color);
  };

  // Get custom categories (not in default list)
  const defaultKeys = ["health", "career", "family", "relationships", "learning", "spirit"];
  const customCategories = categories 
    ? Object.entries(categories).filter(([key]) => !defaultKeys.includes(key))
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* User Profile */}
          <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-xl">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Your name"
                    autoFocus
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleNameEdit}>
                    <Check className="w-4 h-4 text-emerald-600" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setIsEditingName(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{user?.name}</p>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-6 w-6" 
                    onClick={() => { setEditedName(user?.name || ''); setIsEditingName(true); }}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                </div>
              )}
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>

          {/* Settings Options */}
          <div className="space-y-4">
            {/* Dark Mode */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-muted-foreground">Toggle dark theme</p>
                </div>
              </div>
              <Switch
                checked={user?.settings?.dark_mode || false}
                onCheckedChange={handleDarkModeToggle}
                data-testid="dark-mode-switch"
              />
            </div>

            {/* Week Start Day */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Week Starts Monday</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.settings?.week_starts_monday !== false ? 'Mon-Sun' : 'Sun-Sat'}
                  </p>
                </div>
              </div>
              <Switch
                checked={user?.settings?.week_starts_monday !== false}
                onCheckedChange={handleWeekStartToggle}
                data-testid="week-start-switch"
              />
            </div>

            {/* Reminders */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Reminders</p>
                  <p className="text-sm text-muted-foreground">Daily reminder notifications</p>
                </div>
              </div>
              <Switch
                checked={user?.settings?.reminders || false}
                onCheckedChange={handleRemindersToggle}
                data-testid="reminders-switch"
              />
            </div>

            {/* Notification Times - only show if reminders enabled */}
            {user?.settings?.reminders !== false && (
              <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <p className="font-medium text-sm">Notification Schedule</p>
                </div>
                
                <TimeInput
                  label="Morning motivation"
                  icon={Sun}
                  iconColor="text-amber-500"
                  value={user?.settings?.morning_notification_time || "08:00"}
                  onChange={(value) => handleNotificationTimeChange("morning_notification_time", value)}
                />
                
                <TimeInput
                  label="Evening reminder"
                  icon={Moon}
                  iconColor="text-indigo-500"
                  value={user?.settings?.evening_reminder_time || "19:00"}
                  onChange={(value) => handleNotificationTimeChange("evening_reminder_time", value)}
                />
                
                <TimeInput
                  label="Nightly summary"
                  icon={PartyPopper}
                  iconColor="text-emerald-500"
                  value={user?.settings?.nightly_summary_time || "21:00"}
                  onChange={(value) => handleNotificationTimeChange("nightly_summary_time", value)}
                />
              </div>
            )}
          </div>

          {/* Categories Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Custom Categories</h3>
              {!isAddingCategory && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAddingCategory(true)}
                  data-testid="add-category-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              )}
            </div>

            {/* Custom categories list */}
            {customCategories.length > 0 && (
              <div className="space-y-2">
                {customCategories.map(([key, cat]) => (
                  <div key={key} className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                    {editingCategory === key ? (
                      <>
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0 cursor-pointer ring-2 ring-offset-2"
                          style={{ backgroundColor: editCategoryColor, ringColor: editCategoryColor }}
                        />
                        <Input
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="flex-1 h-8 text-sm"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          {COLOR_OPTIONS.slice(0, 5).map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setEditCategoryColor(color)}
                              className={`w-5 h-5 rounded-full ${editCategoryColor === color ? 'ring-2 ring-offset-1 ring-primary' : ''}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditCategory(key)}>
                          <Check className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingCategory(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="flex-1 text-sm">{cat.name}</span>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-7 w-7"
                          onClick={() => startEditingCategory(key, cat)}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-7 w-7 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteCategory(key)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {customCategories.length === 0 && !isAddingCategory && (
              <p className="text-sm text-muted-foreground">No custom categories yet</p>
            )}

            {/* Add category form */}
            {isAddingCategory && (
              <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
                <Input
                  placeholder="Category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  data-testid="category-name-input"
                />
                
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Pick a color</p>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewCategoryColor(color)}
                        className={`w-8 h-8 rounded-full transition-transform ${
                          newCategoryColor === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingCategory(false);
                      setNewCategoryName("");
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddCategory}
                    className="flex-1"
                    data-testid="save-category-btn"
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Sign Out */}
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
